import * as THREE from 'three';

export type VectorTuple = readonly [x: number, y: number, z: number];

export interface MeshTransform {
  readonly position?: VectorTuple;
  readonly rotation?: VectorTuple;
  readonly scale?: VectorTuple | number;
}

export interface MaterialOptions {
  readonly roughness?: number;
  readonly metalness?: number;
  readonly emissive?: THREE.ColorRepresentation;
  readonly emissiveIntensity?: number;
  readonly side?: THREE.Side;
}

export function lowPolyMaterial(
  name: string,
  color: THREE.ColorRepresentation,
  options: MaterialOptions = {},
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color,
    roughness: options.roughness ?? 0.94,
    metalness: options.metalness ?? 0,
    emissive: options.emissive ?? 0x000000,
    emissiveIntensity: options.emissiveIntensity ?? 0,
    flatShading: true,
    side: options.side ?? THREE.FrontSide,
  });
  material.name = name;
  return material;
}

export function addMesh(
  parent: THREE.Object3D,
  name: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  transform: MeshTransform = {},
): THREE.Mesh {
  geometry.name ||= `${name}-geometry`;
  const result = new THREE.Mesh(geometry, material);
  result.name = name;
  if (transform.position) result.position.set(...transform.position);
  if (transform.rotation) result.rotation.set(...transform.rotation);
  if (typeof transform.scale === 'number') {
    result.scale.setScalar(transform.scale);
  } else if (transform.scale) {
    result.scale.set(...transform.scale);
  }
  result.castShadow = true;
  result.receiveShadow = true;
  parent.add(result);
  return result;
}

export function addBeam(
  parent: THREE.Object3D,
  name: string,
  start: VectorTuple,
  end: VectorTuple,
  radius: number,
  material: THREE.Material,
  endRadius = radius,
  radialSegments = 6,
): THREE.Mesh {
  const startPoint = new THREE.Vector3(...start);
  const endPoint = new THREE.Vector3(...end);
  const direction = endPoint.clone().sub(startPoint);
  const length = direction.length();
  const beam = addMesh(
    parent,
    name,
    new THREE.CylinderGeometry(endRadius, radius, length, radialSegments),
    material,
  );
  beam.position.copy(startPoint).add(endPoint).multiplyScalar(0.5);
  beam.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return beam;
}

export function createGableGeometry(width: number, height: number, depth: number): THREE.BufferGeometry {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const positions = new Float32Array([
    -halfWidth, 0, halfDepth,
    halfWidth, 0, halfDepth,
    0, height, halfDepth,
    -halfWidth, 0, -halfDepth,
    0, height, -halfDepth,
    halfWidth, 0, -halfDepth,
  ]);
  const indices = [
    0, 1, 2,
    3, 5, 4,
    0, 3, 1,
    1, 3, 5,
    0, 2, 3,
    3, 2, 4,
    2, 1, 4,
    4, 1, 5,
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createPennantGeometry(length: number, height: number): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([
      0, 0, 0,
      length, -height * 0.35, 0,
      0, -height, 0,
    ], 3),
  );
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function createTrapezoidGeometry(length: number, nearHeight: number, farHeight: number): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute([
      0, nearHeight / 2, 0,
      0, -nearHeight / 2, 0,
      length, farHeight / 2, 0,
      length, farHeight / 2, 0,
      0, -nearHeight / 2, 0,
      length, -farHeight / 2, 0,
    ], 3),
  );
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

export function finishModel(
  id: string,
  name: string,
  biome: string,
  sculpture: THREE.Group,
): THREE.Group {
  sculpture.name = `${id}-sculpture`;
  sculpture.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(sculpture);
  if (bounds.isEmpty()) throw new Error(`Model "${id}" has no visible geometry.`);
  const center = bounds.getCenter(new THREE.Vector3());
  sculpture.position.x -= center.x;
  sculpture.position.y -= bounds.min.y;
  sculpture.position.z -= center.z;

  const model = new THREE.Group();
  model.name = `model-${id}`;
  model.userData = { modelId: id, modelName: name, biome };
  model.add(sculpture);
  model.updateMatrixWorld(true);
  return model;
}
